import { Phone } from 'lucide-react';
import whatsappIcon from '@/shared/assets/icons/whatsapp.svg';
import { cn } from '@/shared/utils/cn';
import type { PdpDeliveryPresentationVm } from '../viewModels/pdpDelivery.vm';

interface PdpStoreAvailabilityProps {
  delivery: PdpDeliveryPresentationVm;
  designName: string;
  productUrl: string;
  onViewAllDesigns: () => void;
  onViewInventory: () => void;
}

export function PdpStoreAvailability({
  delivery,
  designName,
  productUrl,
  onViewAllDesigns,
  onViewInventory,
}: PdpStoreAvailabilityProps) {
  const hasInventoryStores = Boolean(delivery.storeName);
  const hasNearbyStores = delivery.nearbyStoresList.length > 0;

  if (!hasInventoryStores && !hasNearbyStores) return null;

  const whatsappText = encodeURIComponent(
    `Hi! Need information on the product ${designName} | ${productUrl}`
  );

  return (
    <div className="mt-3 rounded border border-dashed border-gray-200 p-2.5 text-sm text-gray-900">
      {hasInventoryStores && (
        <div className={delivery.isDirectContactAllowed ? 'relative max-md:pr-[74px]' : ''}>
          {delivery.isDirectContactAllowed && (
            <div className="absolute right-0 top-0 flex gap-1 md:hidden">
              {delivery.contactNumber && (
                <a
                  href={`tel:${delivery.contactNumber}`}
                  className="px-1.5 text-[#59ac59]"
                  aria-label="Call store"
                >
                  <Phone className="h-5 w-5 rotate-90" strokeWidth={1.75} />
                </a>
              )}
              {delivery.whatsappNumber && (
                <a
                  href={`https://api.whatsapp.com/send?phone=91${delivery.whatsappNumber}&text=${whatsappText}`}
                  className="px-1.5 text-[#59ac59]"
                  aria-label="WhatsApp store"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img
                    src={whatsappIcon}
                    alt=""
                    aria-hidden
                    className="size-6 object-contain"
                  />
                </a>
              )}
            </div>
          )}
          <p>
            Available in{' '}
            {delivery.nearbyStoreCount > 0 && (
              <button
                type="button"
                onClick={onViewInventory}
                className="text-[var(--link-blue)] hover:underline"
              >
                {delivery.nearbyStoreCount} store(s)
              </button>
            )}{' '}
            near you. Find it at <strong>{delivery.storeName}</strong>, your closest location!
          </p>
        </div>
      )}
      {hasNearbyStores && (
        <button
          type="button"
          onClick={onViewAllDesigns}
          className={cn(
            'text-[var(--link-blue)] hover:underline',
            hasInventoryStores && 'mt-1'
          )}
        >
          View all designs in stores near you
        </button>
      )}
    </div>
  );
}
